import { useEventListener } from "@react-hookz/web";
import { ChildrenOnly } from "common/types";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { ThemeProvider } from "styled-components";

export const OrientationContext = createContext<
  OrientationContextType | undefined
>(undefined);

export type OrientationContextType = {
  deg: string;
  rotatedToSide: boolean;
  vw: "vh" | "vw";
  vh: "vh" | "vw";
  landscape: boolean;
};

export const OrientationContextProvider = ({ children }: ChildrenOnly) => {
  const { search } = useLocation();
  const deg = useMemo(() => {
    const match = search.match(/[?&]orientation=([0-9]*?)(&|$)/);
    if (!match) return "0";
    return match[1];
  }, [search]);

  const rotatedToSide = deg === "90" || deg === "270";

  const getSpace = useCallback(
    () =>
      rotatedToSide
        ? { width: window.innerHeight, height: window.innerWidth }
        : { width: window.innerWidth, height: window.innerHeight },
    [rotatedToSide]
  );
  const [space, setAvailableSpace] = useState(getSpace());
  const handleResize = useCallback(() => {
    setAvailableSpace(getSpace());
  }, [getSpace]);
  useEventListener(window, "resize", handleResize);

  const landscape = space.width >= space.height;
  const state = useMemo(
    () => ({
      deg,
      rotatedToSide,
      vw: rotatedToSide ? ("vh" as const) : ("vw" as const),
      vh: rotatedToSide ? ("vw" as const) : ("vh" as const),
      landscape,
    }),
    [deg, rotatedToSide, landscape]
  );

  return (
    <OrientationContext.Provider value={state}>
      <ThemeProvider theme={state}>{children}</ThemeProvider>
    </OrientationContext.Provider>
  );
};

export const useOrientation = (): OrientationContextType => {
  const context = useContext(OrientationContext);
  if (context === undefined)
    throw new Error(
      "useOrientation must be used within OrientationContextProvider"
    );
  return context;
};
